import withLoadingPage from 'HOCs/withLoadingPage';
import NoticeIcon from 'assets/icons/notice-yellow-icon.svg';
import CollapseSidebarProcessStepsLayout from 'components/common/CollapseSidebarProcessStepsLayout';
import InformPopup from 'components/common/InformPopup';
import LoadingScreen from 'components/common/LoadingScreen/ver2';
import { PORTAL_NAME_MODAL_DEFINE } from 'components/common/PortalModal/config';
import ConfirmInformation from 'components/fx/FxFormAddTransaction/ConfirmInformation';
import { FORM_TRANSACTION_DETAILS_MODAL } from 'components/fx/FxFormAddTransaction/FormTransactionDetails';
import { FORM_TRANSACTION_SUMMARY_MODAL } from 'components/fx/FxFormAddTransaction/FormTransactionSummary';
import Order from 'components/fx/FxFormAddTransaction/Order';
import PaymentMethod from 'components/fx/FxFormAddTransaction/PaymentMethod';
import PickupDate from 'components/fx/FxFormAddTransaction/PickupDate';
import PickupLocation from 'components/fx/FxFormAddTransaction/PickupLocation';
import { FORM_TRANSACTION_SUMMARY_SUCCESS_MODAL } from 'components/fx/FxFormAddTransaction/TransactionSummary';
import ConfirmInformationAsGuest from 'components/fx/FxFormAddTransactionAsGuest/ConfirmInformationAsGuest';
import DocumentUpload from 'components/fx/FxFormAddTransactionAsGuest/DocumentUpload';
import { FORM_TRANSACTION_DETAILS_MODAL_2 } from 'components/fx/components/FormTransactionDetails';
import {
  DESKTOP_MIN_WIDTH,
  FX_TRANSACTIONS_PREFIX,
  STATUS_CANCELLED,
  STATUS_FAIL,
  STATUS_SUCCESS,
} from 'constants';
import withShowPIIVerifyNowModal from 'features/pii/HOCs/withShowPIIVerifyNowModal';
import Cookies from 'helpers/cookies';
import useClarity from 'hooks/clarity/useClarity';
import useFxAddTransaction, {
  FX_MAKE_PAYMENT_KEY,
} from 'hooks/fx/useFxAddTransaction';
import useFxCheckInactivityTime from 'hooks/fx/useFxCheckInactivityTime';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useFxPayment from 'hooks/fx/useFxPayment';
import useAuth from 'hooks/useAuth';
import useCommonStore from 'hooks/useCommonStore';
import useCountdownToAction from 'hooks/useCountdownToAction';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import usePortalModal from 'hooks/usePortalModal';
import usePreventBrowserBackButton from 'hooks/usePreventBrowserBackButton';
import { isEmpty, isFunction } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const FxAddTransactionPageWithLoading = withLoadingPage(LoadingScreen);

const FX_ADD_TRANSACTION_EXCHANGE_RATE_OUTDATE_COUNTDOWN_DEFAULT = 600; //600s = 10m

const FxAddTransactionPage = withShowPIIVerifyNowModal(() => {
  const { t } = useLang();
  const { showPortalModalName } = usePortalModal();

  const [isOpenInformPopup, setOpenInformPopup] = useState(false);

  const { isFxAddTransactionPage, isFxAddTransactionAsGuestPage } = usePage();

  // Backing into the abandoned FX flow must land on the FX transactions list, the
  // destination this page's own exits already use (hooks/fx/useFxAddTransaction.jsx:427,
  // hooks/fx/useFxFormAddTransactionStore.jsx:517 and :1582).
  usePreventBrowserBackButton(FX_TRANSACTIONS_PREFIX);

  const {
    isDisabled,
    getNavbarsAndBodyContentOfSidebar,
    getNavbarsAndBodyContentOfSidebarAsGuest,
    fxAddTransactionTotalNumberOfSteps,
    orderRef,
    paymentStatus,
    isMakePayment,
    removeFxMakePayment,
    isFxAsGuest,
    guestBuyAmount,
    guestPayAmount,
    guestCurrencyCode,
  } = useFxAddTransaction();
  const {
    addTransactionCurrentStep: currentStep,
    setAddTransactionCurrentStep,
    refreshCurrencyOrdersByExchangeRateOutdate,
    currencyOrders,
    tokenTransaction,
    transactionInformationStoreTransformInitial,
    transactionDetails,
    fromWebflowRedirectToOrderInitial,
    confirmInformationEmail,
    isForceShowReceiptDetailsScreen,
    handleOnClickCancelPopup,
  } = useFxFormAddTransactionStore();
  const { paymentMintProcess } = useFxPayment();
  const { setPending, setLoadingMessage } = useCommonStore();

  const isForceHideDiscountEligibilityToast = useRef(false);

  const { isAuthenticated } = useAuth();

  const { clarityAPI } = useClarity();
  useEffect(() => {
    clarityAPI.init();
  }, [isAuthenticated]);

  const handleOnClickCancelWithStop = () => {
    clarityAPI.stop();
    handleOnClickCancelPopup();
  };

  const {
    isOpenInactivityPopup,
    FxInactivityTimePopup,
    handleOnClickInactivityTimePopup,
  } = useFxCheckInactivityTime();

  const navbarsAndBodyContentOfSidebar = !isFxAsGuest
    ? getNavbarsAndBodyContentOfSidebar()
    : getNavbarsAndBodyContentOfSidebarAsGuest();

  /* BEGIN - check rate outdate */
  const formTransactionSummaryEle = document.getElementsByClassName(
    FORM_TRANSACTION_SUMMARY_MODAL,
  )?.[0];
  const transactionSummaryEle = document.getElementsByClassName(
    FORM_TRANSACTION_SUMMARY_SUCCESS_MODAL,
  )?.[0];
  const formTransactionDetails1Ele = document.getElementsByClassName(
    FORM_TRANSACTION_DETAILS_MODAL,
  )?.[0];
  const formTransactionDetails2Ele = document.getElementsByClassName(
    FORM_TRANSACTION_DETAILS_MODAL_2,
  )?.[0];
  const handleExchangeRateOutdate = async (loopCallback) => {
    refreshCurrencyOrdersByExchangeRateOutdate({
      callback: loopCallback,
    });
  };

  useCountdownToAction({
    countdownInit: 0,
    isRun:
      !isMakePayment &&
      (isFxAddTransactionPage || isFxAddTransactionAsGuestPage) &&
      !isEmpty(currencyOrders) &&
      !formTransactionSummaryEle &&
      !transactionSummaryEle &&
      !formTransactionDetails1Ele &&
      !formTransactionDetails2Ele,
    cb: handleExchangeRateOutdate,
    deps: [
      isMakePayment,
      isFxAddTransactionPage,
      isFxAddTransactionAsGuestPage,
      currencyOrders,
      formTransactionSummaryEle,
      transactionSummaryEle,
      formTransactionDetails1Ele,
      formTransactionDetails2Ele,
    ],
    delaySec: FX_ADD_TRANSACTION_EXCHANGE_RATE_OUTDATE_COUNTDOWN_DEFAULT,
    isLoop: true,
  });
  /* END - check rate outdate */

  /* BEGIN - payment mint */
  useEffect(() => {
    if (tokenTransaction && orderRef) {
      setPending(true);
      paymentMintProcess(orderRef, tokenTransaction, confirmInformationEmail);
    }
  }, [orderRef, tokenTransaction]);
  /* END - payment mint */

  /* BEGIN - add transaction information initial */
  useEffect(() => {
    if (orderRef) {
      transactionInformationStoreTransformInitial({
        orderRef,
        errorCallback: handleOnClickCancelWithStop,
      });

      let currentStep = 0;

      if (isMakePayment) {
        currentStep = fxAddTransactionTotalNumberOfSteps - 1;

        if (paymentStatus === STATUS_CANCELLED) {
          currentStep = fxAddTransactionTotalNumberOfSteps - 1;
          setAddTransactionCurrentStep(currentStep);
          setOpenInformPopup(true);
          return;
        }

        if (paymentStatus === STATUS_FAIL) {
          currentStep = fxAddTransactionTotalNumberOfSteps - 1;
          setAddTransactionCurrentStep(currentStep);
          setOpenInformPopup(true);
          return;
        }

        if (paymentStatus === STATUS_SUCCESS) {
          currentStep = fxAddTransactionTotalNumberOfSteps;
          setAddTransactionCurrentStep(currentStep);
          removeFxMakePayment();
          return;
        }

        setAddTransactionCurrentStep(currentStep);

        if (!orderRef) {
          removeFxMakePayment();
          window.location.reload();
          return;
        }
        return;
      } else {
        if (paymentStatus === STATUS_CANCELLED) {
          currentStep = fxAddTransactionTotalNumberOfSteps - 1;
          setAddTransactionCurrentStep(currentStep);
          setOpenInformPopup(true);
          return;
        }

        if (paymentStatus === STATUS_FAIL) {
          currentStep = fxAddTransactionTotalNumberOfSteps - 1;
          setAddTransactionCurrentStep(currentStep);
          setOpenInformPopup(true);
        }

        if (paymentStatus === STATUS_SUCCESS) {
          currentStep = fxAddTransactionTotalNumberOfSteps;
          setAddTransactionCurrentStep(currentStep);
          removeFxMakePayment();
          return;
        }
      }
    }
  }, [orderRef, paymentStatus, isMakePayment, transactionDetails]);
  /* END - add transaction information initial */

  /* BEGIN - from webflow to order initial */
  useEffect(() => {
    if (guestCurrencyCode && guestPayAmount && guestBuyAmount) {
      fromWebflowRedirectToOrderInitial({
        currencyCode: guestCurrencyCode,
        payAmount: guestPayAmount,
        buyAmount: guestBuyAmount,
      });
    }
  }, [isFxAsGuest, guestCurrencyCode, guestPayAmount, guestBuyAmount]);
  /* END - from webflow to order initial */

  useEffect(() => {
    return () => {
      if (!orderRef && !paymentStatus) {
        Cookies.remove(FX_MAKE_PAYMENT_KEY);
      }
      setLoadingMessage();
    };
  }, [orderRef, paymentStatus]);

  const handleOnToggleCancelPopup = (options) => {
    const { onCloseCallback } = options || {};
    showPortalModalName({
      name: PORTAL_NAME_MODAL_DEFINE.CANCEL_FX_ADD_TRANSACTION_MODAL,
      title: isMakePayment ? '' : t('popup_cancel_fx_add_transaction'),
      content: isMakePayment
        ? t('popup_confirm_back_transaction')
        : t('popup_cancel_fx_add_transaction_desc'),
      action: handleOnClickCancelWithStop,
      actionClose: () => {
        if (isFunction(onCloseCallback)) {
          onCloseCallback();
        }
      },
    });
  };

  const handleOnClickViewPickupLocationPopup = () => {
    setAddTransactionCurrentStep(1);
  };
  const handleOnViewPickupLocation = () => {
    showPortalModalName({
      name: PORTAL_NAME_MODAL_DEFINE.PICKUP_LOCATION_PREVIEW_MODAL,
      action: handleOnClickViewPickupLocationPopup,
    });
  };

  const stepContentReferenceAsRegister = {
    0: (
      <Order
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
        isForceHideDiscountEligibilityToast={
          isForceHideDiscountEligibilityToast
        }
      />
    ),
    1: (
      <PickupLocation
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    2: (
      <PaymentMethod
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    3: (
      <PickupDate
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    4: (
      <ConfirmInformation
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    5: (
      <ConfirmInformation
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
  };
  const stepContentReferenceAsGuest = {
    0: (
      <Order
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
        isForceHideDiscountEligibilityToast={
          isForceHideDiscountEligibilityToast
        }
      />
    ),
    1: (
      <PickupLocation
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    2: (
      <PaymentMethod
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    3: (
      <PickupDate
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    4: (
      <ConfirmInformationAsGuest
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    5: (
      <DocumentUpload
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
    6: (
      <DocumentUpload
        handleOnToggleCancelPopup={handleOnToggleCancelPopup}
        handleOnViewPickupLocation={handleOnViewPickupLocation}
      />
    ),
  };
  const stepContentReference = !isFxAsGuest
    ? stepContentReferenceAsRegister
    : stepContentReferenceAsGuest;

  const renderAddTransactionPage = () => {
    if (isFxAsGuest && isMakePayment && isEmpty(transactionDetails))
      return <FxAddTransactionPageWithLoading />;
    if (isForceShowReceiptDetailsScreen)
      return (
        <AddTransactionStyled>
          {stepContentReference[currentStep]}
        </AddTransactionStyled>
      );
    return (
      <>
        {currentStep <= fxAddTransactionTotalNumberOfSteps && (
          <AddTransactionStyled>
            <CollapseSidebarProcessStepsLayout
              titleSidebar={t('fx_add_transaction_buy_currency_label')}
              orderRef={isMakePayment ? orderRef : ''}
              navbars={navbarsAndBodyContentOfSidebar}
              currentStep={currentStep}
              stepContentReference={stepContentReference}
              isPreview={isDisabled}
              isHideProcessSteps={isDisabled}
              isSuccess={currentStep === fxAddTransactionTotalNumberOfSteps}
            />
          </AddTransactionStyled>
        )}
      </>
    );
  };

  return (
    <>
      {isFxAsGuest && isOpenInactivityPopup && (
        <FxInactivityTimePopup
          isOpen={isFxAsGuest && isOpenInactivityPopup}
          onClick={handleOnClickInactivityTimePopup}
          onClose={handleOnClickCancelWithStop}
        />
      )}

      {isOpenInformPopup && (
        <InformPopup
          isOpen={isOpenInformPopup}
          imageSrc={NoticeIcon}
          imagesStyles={{
            width: '75px',
            height: '60px',
            marginBottom: '16px',
          }}
          text={t('popup_payment_fail')}
          textStyle={{
            color: 'var(--ds-c-grey-dark)',
          }}
          content={t('popup_payment_fail_desc')}
          buttonLabel1={null}
          buttonLabel2={t('button_ok')}
          onClose={() => setOpenInformPopup(false)}
          onClick={() => setOpenInformPopup(false)}
          isBackdropOverlap={false}
        />
      )}

      {renderAddTransactionPage()}
    </>
  );
});

const AddTransactionStyled = styled.div`
  margin: 70px 0px 0px 0px;
  padding: 16px;
  height: calc(100dvh - 70px);
  width: 100%;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin: unset;
    margin-top: 0px;
    padding: unset;
    padding-top: 96px;
    height: fit-content;
    min-height: 100dvh;
  }
`;

export default FxAddTransactionPage;
